import { ChevronRight, Check, ChevronsDownUp, ChevronsUpDown, type LucideIcon } from "lucide-react"
import { useEffect, useState, useRef, useMemo } from "react"
import { FixedSizeList as List } from "react-window"

import { Button } from "@ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { SearchableProperty } from "@/constants/searchableProperties"

import { formatPropertyName } from "@/lib/properties"
import { cn } from "@/lib/utils"

import { PropertyFacet } from "@/models/facets"

const DEBOUNCE_DELAY = 1000
const MAX_ITEMS_WITHOUT_FILTER = 100
const ITEM_HEIGHT = 40
const MAX_LIST_HEIGHT = 200

interface SearchPropertySectionProps {
    label: string
    Icon?: LucideIcon
    simpleProperties: SearchableProperty[]
    advancedProperties?: PropertyFacet[]
    onSelection?: (selections: Record<string, string> | undefined) => void
    onClear: number
}

const VirtualListItem = ({ index, style, data }: { index: number, style: any, data: any }) => {
    const { items, currentValue, handleSelectChange } = data
    const value = items[index]

    return (
        <div style={style}>
            <Button
                variant="ghost"
                className='flex flex-row items-center justify-between p-2 h-auto w-full'
                onClick={() => handleSelectChange(value._id)}
            >
                <div className="flex items-center justify-between w-full text-left">
                    <span className="truncate flex-1 mr-2 text-sm" title={value._id}>
                        {value._id}
                    </span>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground shrink-0">
                            ({value.count})
                        </span>
                        {currentValue === value._id && <Check className="h-4 w-4" />}
                    </div>
                </div>
            </Button>
        </div>
    )
}

const getCleanedValues = (values: Record<string, string>): Record<string, string> | undefined => {
    const cleanedValues = Object.fromEntries(
        Object.entries(values).filter(([key, value]) =>
            value !== null && value !== undefined && value !== ""
        )
    )

    return Object.keys(cleanedValues).length > 0 ? cleanedValues : undefined
}

export function SearchPropertySection({ label, Icon, simpleProperties, advancedProperties, onSelection, onClear }: Readonly<SearchPropertySectionProps>) {
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    const [propertyValues, setPropertyValues] = useState<Record<string, string>>({})
    const [filterValues, setFilterValues] = useState<Record<string, string>>({})
    const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({})

    const handleOnChange = (propertyId: string, value: string) => {
        const newValues = { ...propertyValues, [propertyId]: value }
        setPropertyValues(newValues)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            const cleanedValues = getCleanedValues(newValues)
            onSelection?.(cleanedValues)
        }, DEBOUNCE_DELAY)
    }

    const handleClear = (propertyId: string) => {
        const newValues = { ...propertyValues, [propertyId]: "" }
        setPropertyValues(newValues)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        const cleanedValues = getCleanedValues(newValues)
        onSelection?.(cleanedValues)
    }

    const handleSelectChange = (propertyId: string, value: string) => {
        const newValues = { ...propertyValues, [propertyId]: value }
        setPropertyValues(newValues)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        setOpenPopovers(prev => ({ ...prev, [propertyId]: false }))

        const cleanedValues = getCleanedValues(newValues)
        onSelection?.(cleanedValues)
    }

    const handleClearSelect = (propertyId: string) => {
        const newValues = { ...propertyValues, [propertyId]: "" }
        setPropertyValues(newValues)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        setOpenPopovers(prev => ({ ...prev, [propertyId]: false }))

        const cleanedValues = getCleanedValues(newValues)
        onSelection?.(cleanedValues)
    }

    const handleFilterChange = (propertyId: string, filterValue: string) => {
        setFilterValues(prev => ({
            ...prev,
            [propertyId]: filterValue
        }))
    }

    const handlePopoverOpenChange = (propertyId: string, open: boolean) => {
        setOpenPopovers(prev => ({ ...prev, [propertyId]: open }))

        if (!open) {
            setFilterValues(prev => ({ ...prev, [propertyId]: "" }))
        }
    }

    const getFilteredValues = useMemo(() => {
        return (property: PropertyFacet) => {
            const filter = filterValues[property.field]?.toLowerCase() || ""
            let filteredValues = property.values.filter(value =>
                value && value._id !== null && value._id !== ""
            )

            if (filter) {
                filteredValues = filteredValues.filter(value =>
                    String(value._id).toLowerCase().includes(filter)
                )
            } else {
                filteredValues = filteredValues
                    .sort((a, b) => (b.count || 0) - (a.count || 0))
                    .slice(0, MAX_ITEMS_WITHOUT_FILTER)
            }

            return filteredValues
        }
    }, [filterValues])

    const getEmptyValueCount = (property: PropertyFacet) => {
        const emptyValue = property.values.find(value => !value._id || value._id === "")
        return emptyValue ? emptyValue.count : null
    }

    const handleOnSelectionChange = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        const cleanedValues = getCleanedValues(propertyValues)
        onSelection?.(cleanedValues)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }

    const handleInputChange = (propertyId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        handleFilterChange(propertyId, e.target.value)
    }

    useEffect(() => {
        setPropertyValues({})
        setFilterValues({})
        setOpenPopovers({})

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
    }, [onClear])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <div className={cn("relative flex w-full min-w-0 flex-col p-2 border-b border-sidebar-border")}>
            <Collapsible
                defaultOpen={false}
                className="group/collapsible"
            >
                <CollapsibleTrigger
                    className={cn(
                        "group/label w-full text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                        "flex h-8 shrink-0 items-center rounded-md px-2 text-sm font-medium outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
                        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 hover:cursor-pointer mb-1",
                    )}
                >
                    {Icon && <Icon className="mr-2" />}
                    {label}
                    {" "}
                    <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="flex flex-col gap-2 pb-2 px-2">
                        {
                            simpleProperties.map((property) => (
                                <div key={property.id}>
                                    <Label className="text-xs">{property.label}</Label>
                                    <Input
                                        clearable
                                        value={propertyValues[property.property] || ""}
                                        onChange={(e) => handleOnChange(property.property, e.target.value)}
                                        onClear={() => handleClear(property.property)}
                                        onKeyDown={(e) => e.key === "Enter" && handleOnSelectionChange()}
                                        placeholder={property.placeholder}
                                        className="bg-white shadow-none hover:shadow-sm"
                                    />
                                </div>
                            ))
                        }
                        {
                            advancedProperties?.map((property) => {
                                const filteredValues = getFilteredValues(property)
                                const hasFilter = filterValues[property.field]?.length > 0
                                const emptyValueCount = getEmptyValueCount(property)
                                const isOpen = openPopovers[property.field] || false
                                const currentValue = propertyValues[property.field] || ""
                                const shouldUseVirtualList = filteredValues.length > 50

                                return (
                                    <div key={property.field}>
                                        <Label className="text-xs">{formatPropertyName(property.name)}</Label>
                                        <Popover
                                            open={isOpen}
                                            onOpenChange={(open) => handlePopoverOpenChange(property.field, open)}
                                        >
                                            <PopoverTrigger asChild>
                                                <div className='flex flex-row border gap-2 items-center justify-center cursor-pointer hover:bg-gray-200 rounded-md h-10 px-3 bg-white'>
                                                    <div className='w-full text-sm font-light text-left text-primary truncate'>
                                                        {currentValue || `Select ${formatPropertyName(property.name)}`}
                                                    </div>
                                                    <div className='border-l pl-2'>
                                                        {
                                                            isOpen
                                                                ? <ChevronsDownUp className="h-4 w-4 opacity-50" />
                                                                : <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                                        }
                                                    </div>
                                                </div>
                                            </PopoverTrigger>
                                            <PopoverContent className="max-w-[236px] p-2 border shadow" align="start">
                                                <Input
                                                    placeholder={`Filter ${formatPropertyName(property.name)}...`}
                                                    value={filterValues[property.field] || ""}
                                                    onChange={(e) => handleInputChange(property.field, e)}
                                                    onKeyDown={handleInputKeyDown}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className='mb-2'
                                                />
                                                {
                                                    !hasFilter && (
                                                        <div className="mb-2 text-xs text-muted-foreground px-2">
                                                            Showing top {Math.min(filteredValues.length, MAX_ITEMS_WITHOUT_FILTER)} results. Use filter to search all.
                                                        </div>
                                                    )
                                                }
                                                <div className={`flex flex-col gap-1 ${shouldUseVirtualList ? "" : "max-h-[200px] overflow-y-auto"}`}>
                                                    {
                                                        (!filterValues[property.field] || filterValues[property.field] === "") && (
                                                            <Button
                                                                variant="ghost"
                                                                className='flex flex-row items-center justify-between p-2 h-auto'
                                                                onClick={() => handleClearSelect(property.field)}
                                                                type="button"
                                                            >
                                                                <div className="flex items-center justify-between w-full text-left">
                                                                    <span className="truncate flex-1 mr-2 text-sm">
                                                                        No value
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        {emptyValueCount !== null && (
                                                                            <span className="text-xs text-muted-foreground shrink-0">
                                                                                ({emptyValueCount})
                                                                            </span>
                                                                        )}
                                                                        {currentValue === "" && <Check className="h-4 w-4" />}
                                                                    </div>
                                                                </div>
                                                            </Button>
                                                        )
                                                    }
                                                    {
                                                        filteredValues.length > 0 ? (
                                                            shouldUseVirtualList ? (
                                                                <List
                                                                    width={220}
                                                                    height={Math.min(filteredValues.length * ITEM_HEIGHT, MAX_LIST_HEIGHT)}
                                                                    itemCount={filteredValues.length}
                                                                    itemSize={ITEM_HEIGHT}
                                                                    itemData={{
                                                                        items: filteredValues,
                                                                        currentValue,
                                                                        handleSelectChange: (value: string) => handleSelectChange(property.field, value)
                                                                    }}
                                                                >
                                                                    {VirtualListItem}
                                                                </List>
                                                            ) : (
                                                                filteredValues.map((value) => (
                                                                    <Button
                                                                        key={value._id}
                                                                        variant="ghost"
                                                                        className='flex flex-row items-center justify-between p-2 h-auto'
                                                                        onClick={() => handleSelectChange(property.field, value._id)}
                                                                    >
                                                                        <div className="flex items-center justify-between w-full text-left">
                                                                            <span className="truncate flex-1 mr-2 text-sm" title={value._id}>
                                                                                {value._id}
                                                                            </span>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-xs text-muted-foreground shrink-0">
                                                                                    ({value.count})
                                                                                </span>
                                                                                {currentValue === value._id && <Check className="h-4 w-4" />}
                                                                            </div>
                                                                        </div>
                                                                    </Button>
                                                                ))
                                                            )
                                                        ) : (
                                                            <div className="p-2 text-sm text-muted-foreground text-center">
                                                                No options found
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                )
                            })
                        }
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    )
}